import axios, { AxiosHeaders, type AxiosError, type AxiosResponse } from 'axios';
import { ACCOUNT_ENDPOINTS } from '@/url_constants/accountEndpoints';
import { MOCK_BANK_VERIFICATION } from '@/api/flags';
import { getLatestMockVerifiedAccount, isMockSession } from '@/api/activation';
import { getAccessToken, clearAuthSession } from '@/api/session';
import { loadDb, saveDb } from '@/mock/db';
import type {
  Account,
  AccountSummary,
  AccountStatus,
  Transaction,
  TransactionStatus,
  UserProfile,
} from '@/types/account.types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://banking-dev.devopsark.com/api/v1';

const getAuthHeaders = (): Record<string, string> => {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const accountClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

accountClient.interceptors.request.use((config) => {
  const headers = new AxiosHeaders({ ...config.headers, ...getAuthHeaders() });
  if (config.data instanceof FormData) headers.delete('Content-Type');
  config.headers = headers;
  return config;
});

accountClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string; error?: string }>) => {
    if (error.response?.status === 401 && !isMockSession()) {
      clearAuthSession();
      window.location.href = '/welcome';
    }
    return Promise.reject(error);
  }
);

const extractErrorMessage = (
  error: AxiosError<{ message?: string; error?: string }>,
  fallback: string
): string => {
  const data = error.response?.data;
  if (data && typeof data === 'object') {
    return data.message || data.error || fallback;
  }
  return error.message || fallback;
};

interface ApiEnvelope<T> {
  success?: boolean;
  message?: string;
  data: T;
  correlationId?: string;
  timestamp?: number;
}

function unwrapData<T>(response: AxiosResponse<T | ApiEnvelope<T>>): T {
  const data = response.data as Record<string, unknown>;
  if (data && typeof data === 'object' && 'data' in data && data.data !== undefined) {
    return data.data as T;
  }
  return response.data as T;
}

interface BackendAccountSummary {
  accounts: BackendAccount[];
  customerId?: string;
  totalAccounts?: number;
}

function normalizeAccountSummary(data: unknown): BackendAccountSummary {
  if (Array.isArray(data)) {
    return { accounts: data as BackendAccount[] };
  }
  if (!data || typeof data !== 'object') {
    return { accounts: [] };
  }
  const obj = data as Record<string, unknown>;
  if (Array.isArray(obj.accounts)) {
    return {
      accounts: obj.accounts as BackendAccount[],
      customerId: obj.customerId as string | undefined,
      totalAccounts: obj.totalAccounts as number | undefined,
    };
  }
  if (obj.data && typeof obj.data === 'object') {
    const inner = obj.data as Record<string, unknown>;
    if (Array.isArray(inner.accounts)) {
      return {
        accounts: inner.accounts as BackendAccount[],
        customerId: inner.customerId as string | undefined,
        totalAccounts: inner.totalAccounts as number | undefined,
      };
    }
    if (Array.isArray(inner)) {
      return { accounts: inner as BackendAccount[] };
    }
  }
  return { accounts: [] };
}

interface BackendRecentTransactions {
  transactions: BackendTransaction[];
  accountNumber?: string;
  currency?: string;
  currentBalance?: number;
}

function normalizeRecentTransactions(data: unknown): BackendRecentTransactions {
  if (Array.isArray(data)) {
    return { transactions: data as BackendTransaction[] };
  }
  if (!data || typeof data !== 'object') {
    return { transactions: [] };
  }
  const obj = data as Record<string, unknown>;
  if (Array.isArray(obj.transactions)) {
    return {
      transactions: obj.transactions as BackendTransaction[],
      accountNumber: obj.accountNumber as string | undefined,
      currency: obj.currency as string | undefined,
      currentBalance: obj.currentBalance as number | undefined,
    };
  }
  if (obj.data && typeof obj.data === 'object') {
    const inner = obj.data as Record<string, unknown>;
    if (Array.isArray(inner.transactions)) {
      return {
        transactions: inner.transactions as BackendTransaction[],
        accountNumber: inner.accountNumber as string | undefined,
        currency: inner.currency as string | undefined,
        currentBalance: inner.currentBalance as number | undefined,
      };
    }
    if (Array.isArray(inner)) {
      return { transactions: inner as BackendTransaction[] };
    }
  }
  return { transactions: [] };
}

interface BackendAccount {
  id: string;
  accountNumber: string;
  customerId: string;
  accountType: string;
  branchName: string;
  ifsc: string;
  currency: string;
  status: string;
  availableBalance: number;
  ledgerBalance: number;
  updatedAt: string;
}

const mapAccountStatus = (status: string): AccountStatus => {
  switch (status) {
    case 'ACTIVE':
      return 'ACTIVE';
    case 'FROZEN':
      return 'FROZEN';
    case 'DORMANT':
      return 'DORMANT';
    case 'CLOSED':
      return 'CLOSED';
    case 'PENDING_ACTIVATION':
      return 'PENDING_ACTIVATION';
    default:
      return 'INACTIVE';
  }
};

const mapBackendAccount = (account: BackendAccount, index: number): Account => ({
  id: account.id || String(index),
  accountType: account.accountType || 'Account',
  accountNumber: account.accountNumber || '',
  branch: account.branchName || '-',
  branchName: account.branchName || '-',
  ifsc: account.ifsc || '-',
  currency: account.currency || 'INR',
  availableBalance: account.availableBalance ?? 0,
  ledgerBalance: account.ledgerBalance ?? 0,
  status: mapAccountStatus(account.status),
  lastRefreshed: account.updatedAt || new Date().toISOString(),
  updatedAt: account.updatedAt || new Date().toISOString(),
  isPrimary: index === 0,
});

export const getAccountSummary = async (customerId: string): Promise<AccountSummary> => {
  // Dev dummy bank verification: the account was approved locally, not on the
  // backend — serve the mock savings account so the dashboard shows it.
  if (MOCK_BANK_VERIFICATION) {
    const mock = getLatestMockVerifiedAccount() ?? {
      reference: 'MOCK-LOGIN',
      customerId: localStorage.getItem('customerId') ?? 'CUS-MOCK-001',
      fullName: 'NovaBank Demo Customer',
      accountType: 'SAVINGS',
      accountNumber: '100000000001',
      ifsc: 'NOVB0000001',
      branchName: 'NovaBank Demo Branch',
      activatedAt: new Date().toISOString(),
      maskedEmail: 'customer@novabank.test',
      maskedMobile: '+91 98xxx xx210',
    };
    if (isMockSession() || getLatestMockVerifiedAccount()) {
      // Share one balance with the mock payments ledger so a payment debits
      // the number the dashboard shows. Seed once (₹25,000); after that the
      // ledger is the source of truth (same rule as getPaymentContext). A
      // seeded-but-empty ledger with no history counts as never seeded.
      const db = loadDb();
      const neverUsed = db.payments.length === 0 && db.ledger.length === 0;
      if (!db.seeded || (db.balancePaise === 0 && neverUsed)) {
        db.seeded = true;
        db.balancePaise = 2_500_000;
        saveDb(db);
      }
      const balance = db.balancePaise / 100;
      const account = mapBackendAccount(
        {
          id: mock.reference,
          accountNumber: mock.accountNumber,
          customerId: mock.customerId,
          accountType: mock.accountType,
          branchName: mock.branchName,
          ifsc: mock.ifsc,
          currency: 'INR',
          status: 'ACTIVE',
          availableBalance: balance,
          ledgerBalance: balance,
          updatedAt: mock.activatedAt,
        },
        0
      );
      return {
        accounts: [account],
        primaryAccount: account,
        totalBalance: account.availableBalance,
        currency: 'INR',
      };
    }
  }
  try {
    const response: AxiosResponse<unknown> = await accountClient.get(
      ACCOUNT_ENDPOINTS.ACCOUNT_SUMMARY(customerId)
    );
    const raw = unwrapData(response);
    const summary = normalizeAccountSummary(raw);
    console.log('[accountApi] accountSummary raw:', raw);
    console.log('[accountApi] accountSummary normalized:', summary);

    const accounts = summary.accounts.map(mapBackendAccount);
    const primaryAccount = accounts.find((a) => a.isPrimary) || accounts[0] || null;

    return {
      accounts,
      primaryAccount,
      totalBalance: accounts.reduce((sum, account) => sum + account.availableBalance, 0),
      currency: accounts[0]?.currency || 'INR',
    };
  } catch (error) {
    console.error('[accountApi] getAccountSummary failed:', error);
    throw new Error(
      extractErrorMessage(
        error as AxiosError<{ message?: string; error?: string }>,
        'Failed to load account summary'
      )
    );
  }
};

interface BackendTransaction {
  id: string;
  transactionId: string;
  referenceNumber: string;
  transactionType: string;
  channel: string;
  status: string;
  amount: number;
  currency: string;
  sourceAccountNumber: string;
  destinationAccountNumber: string;
  description: string;
  narration: string;
  valueDate: string;
  runningBalance: number;
  initiatedAt: string;
  completedAt: string;
  failureReason: string;
}

const mapTransactionStatus = (status: string): TransactionStatus => {
  switch (status.toUpperCase()) {
    case 'SUCCESS':
    case 'COMPLETED':
    case 'EXECUTED':
      return 'SUCCESS';
    case 'FAILED':
    case 'FAILURE':
      return 'FAILED';
    case 'REJECTED':
      return 'REJECTED';
    case 'CANCELLED':
      return 'CANCELLED';
    case 'PENDING_APPROVAL':
      return 'PENDING_APPROVAL';
    case 'PENDING':
    case 'INITIATED':
    default:
      return 'PENDING';
  }
};

const formatBackendDateTime = (isoString: string): { date: string; time: string } => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return { date: isoString, time: '' };
  }
  const [datePart, timePart] = date.toISOString().split('T');
  return {
    date: datePart,
    time: timePart.slice(0, 8),
  };
};

const mapBackendTransaction = (transaction: BackendTransaction): Transaction => {
  const { date, time } = formatBackendDateTime(transaction.initiatedAt || transaction.valueDate);
  const isCredit = transaction.transactionType === 'CREDIT';
  const beneficiary = isCredit
    ? transaction.sourceAccountNumber
    : transaction.destinationAccountNumber;

  return {
    id: transaction.id || transaction.transactionId || '',
    title: transaction.description || transaction.narration || transaction.transactionType || '-',
    beneficiary: beneficiary || '-',
    date,
    time,
    amount: transaction.amount ?? 0,
    type: isCredit ? 'CREDIT' : 'DEBIT',
    status: mapTransactionStatus(transaction.status),
    category: transaction.channel || transaction.transactionType || '-',
    icon: isCredit ? 'arrow-down' : 'arrow-up',
  };
};

export const getRecentTransactions = async (accountNumber: string): Promise<Transaction[]> => {
  // Dev dummy bank verification: the mock account has no backend transactions.
  if (MOCK_BANK_VERIFICATION) {
    const mock = getLatestMockVerifiedAccount();
    if (mock && mock.accountNumber === accountNumber) return [];
  }
  try {
    const response: AxiosResponse<unknown> = await accountClient.get(
      ACCOUNT_ENDPOINTS.RECENT_TRANSACTIONS(accountNumber)
    );
    const raw = unwrapData(response);
    const recent = normalizeRecentTransactions(raw);
    console.log('[accountApi] recentTransactions raw:', raw);
    console.log('[accountApi] recentTransactions normalized:', recent);
    return recent.transactions.map(mapBackendTransaction);
  } catch (error) {
    console.error('[accountApi] getRecentTransactions failed:', error);
    throw new Error(
      extractErrorMessage(
        error as AxiosError<{ message?: string; error?: string }>,
        'Failed to load recent transactions'
      )
    );
  }
};



export interface UserMeData {
  id: string;
  username: string;
  fullName: string;
  email: string;
  mobileNumber: string;
  customerId: string;
  profilePhotoUrl: string;
  status: string;
  customerState?: string;
  role?: string;
  kycStatus?: string;
  preferredLanguage?: string;
  emailVerified: boolean;
  mobileVerified: boolean;
  lastLoginAt: string;
  roles: string[];
}

export const getUserProfile = async (): Promise<UserProfile> => {
  // Dev mock session (dummy bank verification): there is no backend user, so
  // serve the profile from the mock-approved account record.
  if (MOCK_BANK_VERIFICATION && isMockSession()) {
    const mock = getLatestMockVerifiedAccount() ?? {
      customerId: localStorage.getItem('customerId') ?? 'CUS-MOCK-001',
      fullName: 'NovaBank Demo Customer',
      maskedEmail: 'customer@novabank.test',
      maskedMobile: '+91 98xxx xx210',
      activatedAt: new Date().toISOString(),
    };
    if (mock) {
      return {
        id: mock.customerId,
        username: mock.maskedEmail || mock.fullName,
        fullName: mock.fullName,
        email: mock.maskedEmail ?? '',
        mobileNumber: mock.maskedMobile ?? '',
        customerId: mock.customerId,
        profilePhotoUrl: '',
        status: 'ACTIVE',
        emailVerified: true,
        mobileVerified: true,
        lastLoginAt: mock.activatedAt,
        roles: ['CUSTOMER'],
      };
    }
  }
  try {
    const profileResponse: AxiosResponse<UserMeData | ApiEnvelope<UserMeData>> =
      await accountClient.get(ACCOUNT_ENDPOINTS.PROFILE);
    const profileData = unwrapData(profileResponse);
    console.log('[accountApi] /users/me response:', profileData);

    // The profile's customerId is the one the accounts service recognises —
    // keep localStorage in sync so every downstream call uses it.
    if (profileData.customerId) {
      localStorage.setItem('customerId', profileData.customerId);
    }

    return profileData;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;
    console.error('[accountApi] getUserProfile failed:', axiosError.response?.status, axiosError.response?.data);
    throw new Error(extractErrorMessage(axiosError, 'Failed to load profile'));
  }
};

export const uploadUserProfilePhoto = async (photo: File): Promise<UserProfile> => {
  const formData = new FormData();
  formData.append('photo', photo);

  try {
    const response: AxiosResponse<UserMeData | ApiEnvelope<UserMeData>> =
      await accountClient.put(ACCOUNT_ENDPOINTS.PROFILE_PHOTO, formData);
    const profile = unwrapData(response);
    if (profile.customerId) localStorage.setItem('customerId', profile.customerId);
    return profile;
  } catch (error) {
    throw new Error(
      extractErrorMessage(
        error as AxiosError<{ message?: string; error?: string }>,
        'Failed to upload profile photo',
      ),
    );
  }
};

export const logout = async (): Promise<void> => {
  try {
    await accountClient.post(ACCOUNT_ENDPOINTS.LOGOUT);
  } catch (error) {
    throw new Error(
      extractErrorMessage(error as AxiosError<{ message?: string; error?: string }>, 'Logout failed')
    );
  } finally {
    clearAuthSession();
  }
};

export class DepositSlipError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'DepositSlipError';
    this.code = code;
  }
}

/**
 * Fetches the blank deposit slip PDF generated by the backend.
 * Public endpoint — no account lookup or id required.
 */
export const downloadDepositSlipPdf = async (): Promise<Blob> => {
  try {
    const response: AxiosResponse<Blob> = await accountClient.get(
      ACCOUNT_ENDPOINTS.DEPOSIT_SLIP,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;
    throw new DepositSlipError(
      extractErrorMessage(
        axiosError,
        'Unable to download deposit slip. Please try again.'
      ),
      'DOWNLOAD_FAILED'
    );
  }
};
